/**
 * Web-to-app seamless handoff — operator UI (feature #182).
 *
 * Composition:
 *   - Header with emerald->sky gradient + subtitle.
 *   - Create form: campaign_id + web fallback URL + iOS + Android inputs.
 *   - Summary row: total / active / with iOS / with Android.
 *   - Configs table: campaign, platforms, web fallback, actions.
 *   - Outcome funnel card + recent outcomes table.
 *   - Empty + loading states, toast feedback via Sonner.
 *
 * Data layer: TanStack Query for fetch + mutations. Interstitial preview
 * is a plain `<a target="_blank">` pointing at `buildHandoffUrl(campaign_id)`.
 *
 * Brand: emerald->sky headline gradient, zinc borders, 6-8px rounded, light
 * theme only.
 */

import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	buildHandoffUrl,
	computeConfigSummary,
	computeFunnelShares,
	createWebToAppConfig,
	deleteWebToAppConfig,
	fetchWebToAppConfigs,
	fetchWebToAppOutcomes,
	formatInteger,
	formatOutcomeLabel,
	formatTimestamp,
	isValidAndroidPackage,
	isValidCampaignId,
	isValidHttpUrl,
	isValidIosAppId,
	isValidOptionalUrl,
	patchWebToAppConfig,
	sortConfigRows,
	webToAppQueryKeys,
	type HandoffOutcome,
	type WebToAppConfigRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — zinc borders, 6-8px rounded, emerald->sky gradient.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_ACTIVE =
	"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700";
const BADGE_INACTIVE =
	"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-600";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-500 bg-clip-text text-transparent";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WebToAppPageProps {
	/** Optional campaign filter for the outcomes panel. */
	initialCampaign?: string;
}

export function WebToAppPage(props: WebToAppPageProps = {}) {
	const queryClient = useQueryClient();

	const initialCampaign =
		props.initialCampaign && isValidCampaignId(props.initialCampaign)
			? props.initialCampaign
			: "";

	const [outcomeCampaign, setOutcomeCampaign] =
		useState<string>(initialCampaign);
	const [pendingCampaign, setPendingCampaign] =
		useState<string>(initialCampaign);

	// Create form state.
	const [newCampaign, setNewCampaign] = useState<string>("");
	const [newWebUrl, setNewWebUrl] = useState<string>("");
	const [newIosAppId, setNewIosAppId] = useState<string>("");
	const [newAndroidPackage, setNewAndroidPackage] = useState<string>("");
	const [newIosLink, setNewIosLink] = useState<string>("");
	const [newAndroidLink, setNewAndroidLink] = useState<string>("");

	// -------------------------------------------------------------------
	// Queries.
	// -------------------------------------------------------------------
	const configsQuery = useQuery({
		queryKey: webToAppQueryKeys.configs(),
		queryFn: () => fetchWebToAppConfigs(),
	});

	const outcomesQuery = useQuery({
		queryKey: webToAppQueryKeys.outcomes(outcomeCampaign || undefined),
		queryFn: () =>
			fetchWebToAppOutcomes(outcomeCampaign || undefined, 100),
	});

	const rows = useMemo<WebToAppConfigRow[]>(
		() => sortConfigRows(configsQuery.data?.rows ?? []),
		[configsQuery.data?.rows],
	);
	const summary = useMemo(() => computeConfigSummary(rows), [rows]);
	const funnel = outcomesQuery.data?.funnel ?? {
		deep_link_attempted: 0,
		app_store_redirect: 0,
		web_landing: 0,
		total: 0,
	};
	const funnelShares = useMemo(() => computeFunnelShares(funnel), [funnel]);

	// -------------------------------------------------------------------
	// Mutations.
	// -------------------------------------------------------------------
	const createMutation = useMutation({
		mutationFn: (args: Parameters<typeof createWebToAppConfig>[0]) =>
			createWebToAppConfig(args),
		onSuccess: () => {
			toast.success("Handoff config saved.");
			setNewCampaign("");
			setNewWebUrl("");
			setNewIosAppId("");
			setNewAndroidPackage("");
			setNewIosLink("");
			setNewAndroidLink("");
			queryClient.invalidateQueries({
				queryKey: webToAppQueryKeys.configs(),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to save handoff config."),
	});

	const patchMutation = useMutation({
		mutationFn: ({
			id,
			patch,
		}: {
			id: string;
			patch: Parameters<typeof patchWebToAppConfig>[1];
		}) => patchWebToAppConfig(id, patch),
		onSuccess: () => {
			toast.success("Handoff config updated.");
			queryClient.invalidateQueries({
				queryKey: webToAppQueryKeys.configs(),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to update config."),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteWebToAppConfig(id),
		onSuccess: () => {
			toast.success("Handoff config removed.");
			queryClient.invalidateQueries({
				queryKey: webToAppQueryKeys.configs(),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message || "Unable to delete config."),
	});

	const busy =
		createMutation.isPending ||
		patchMutation.isPending ||
		deleteMutation.isPending;

	// -------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------
	const handleApplyCampaign = useCallback(() => {
		const trimmed = pendingCampaign.trim();
		if (trimmed.length === 0) {
			setOutcomeCampaign("");
			return;
		}
		if (!isValidCampaignId(trimmed)) {
			toast.error("Enter a valid campaign id.");
			return;
		}
		setOutcomeCampaign(trimmed);
	}, [pendingCampaign]);

	const handleCreate = useCallback(() => {
		const campaign = newCampaign.trim();
		if (!isValidCampaignId(campaign)) {
			toast.error("Campaign id must be url-safe.");
			return;
		}
		const web = newWebUrl.trim();
		if (!isValidHttpUrl(web)) {
			toast.error("Web fallback must be an http(s) URL.");
			return;
		}
		if (!isValidIosAppId(newIosAppId)) {
			toast.error("iOS app id must be numeric or an https URL.");
			return;
		}
		if (!isValidAndroidPackage(newAndroidPackage)) {
			toast.error(
				"Android package must be reverse-DNS or an https URL.",
			);
			return;
		}
		if (!isValidOptionalUrl(newIosLink)) {
			toast.error("iOS universal link must be an http(s) URL.");
			return;
		}
		if (!isValidOptionalUrl(newAndroidLink)) {
			toast.error("Android app link must be an http(s) URL.");
			return;
		}
		createMutation.mutate({
			campaign_id: campaign,
			web_fallback_url: web,
			ios_app_id: newIosAppId.trim(),
			android_package: newAndroidPackage.trim(),
			ios_universal_link: newIosLink.trim(),
			android_app_link: newAndroidLink.trim(),
		});
	}, [
		newCampaign,
		newWebUrl,
		newIosAppId,
		newAndroidPackage,
		newIosLink,
		newAndroidLink,
		createMutation,
	]);

	const handleToggleActive = useCallback(
		(row: WebToAppConfigRow) => {
			if (busy) return;
			patchMutation.mutate({
				id: row.id,
				patch: { active: !row.active },
			});
		},
		[busy, patchMutation],
	);

	const handleDelete = useCallback(
		(row: WebToAppConfigRow) => {
			if (busy) return;
			deleteMutation.mutate(row.id);
		},
		[busy, deleteMutation],
	);

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------
	const configsError = configsQuery.error as Error | undefined;
	const outcomesError = outcomesQuery.error as Error | undefined;

	return (
		<section className="flex flex-col gap-4">
			<header className="flex flex-col gap-1">
				<h1 className={`text-2xl font-semibold ${HEADLINE}`}>
					Web-to-app handoff
				</h1>
				<p className="text-sm text-zinc-600">
					Configure per-campaign deep links. Mobile users get routed
					into the installed app when possible, with automatic
					fallback to the app store or web landing page.
				</p>
			</header>

			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Add a new handoff
				</h2>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-campaign" className={LABEL}>
							Campaign ID
						</label>
						<input
							id="w2a-new-campaign"
							type="text"
							value={newCampaign}
							onChange={(e) => setNewCampaign(e.target.value)}
							className={INPUT}
							placeholder="summer_2026"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-web" className={LABEL}>
							Web fallback URL
						</label>
						<input
							id="w2a-new-web"
							type="url"
							value={newWebUrl}
							onChange={(e) => setNewWebUrl(e.target.value)}
							className={INPUT}
							placeholder="https://example.com/landing"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-ios-app" className={LABEL}>
							iOS App Store ID (optional)
						</label>
						<input
							id="w2a-new-ios-app"
							type="text"
							value={newIosAppId}
							onChange={(e) => setNewIosAppId(e.target.value)}
							className={INPUT}
							placeholder="123456789"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-android-pkg" className={LABEL}>
							Android package (optional)
						</label>
						<input
							id="w2a-new-android-pkg"
							type="text"
							value={newAndroidPackage}
							onChange={(e) =>
								setNewAndroidPackage(e.target.value)
							}
							className={INPUT}
							placeholder="com.example.app"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-ios-link" className={LABEL}>
							iOS Universal Link (optional)
						</label>
						<input
							id="w2a-new-ios-link"
							type="url"
							value={newIosLink}
							onChange={(e) => setNewIosLink(e.target.value)}
							className={INPUT}
							placeholder="https://links.example.com/ios/..."
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="w2a-new-android-link" className={LABEL}>
							Android App Link (optional)
						</label>
						<input
							id="w2a-new-android-link"
							type="url"
							value={newAndroidLink}
							onChange={(e) => setNewAndroidLink(e.target.value)}
							className={INPUT}
							placeholder="https://links.example.com/android/..."
						/>
					</div>
				</div>
				<div className="flex justify-end">
					<button
						type="button"
						onClick={handleCreate}
						disabled={
							busy ||
							newCampaign.trim().length === 0 ||
							newWebUrl.trim().length === 0
						}
						className={BTN_PRIMARY}
					>
						{createMutation.isPending ? "Saving..." : "Save config"}
					</button>
				</div>
			</div>

			<SummaryRow summary={summary} />

			<div className={`${CARD} overflow-hidden`}>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Handoff configs
					</h2>
					<span className="text-xs text-zinc-500">
						{formatInteger(rows.length)} total
					</span>
				</div>
				{configsQuery.isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						Loading handoff configs...
					</div>
				) : configsError ? (
					<div className="px-4 py-8 text-center text-sm text-red-600">
						{configsError.message || "Unable to load configs."}
					</div>
				) : rows.length === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						No handoff configs yet. Use the form above to add one.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2">
										Campaign
									</th>
									<th scope="col" className="px-4 py-2">
										iOS
									</th>
									<th scope="col" className="px-4 py-2">
										Android
									</th>
									<th scope="col" className="px-4 py-2">
										Web fallback
									</th>
									<th scope="col" className="px-4 py-2">
										Active
									</th>
									<th scope="col" className="px-4 py-2 text-right">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{rows.map((row) => (
									<ConfigRow
										key={row.id}
										row={row}
										busy={busy}
										onToggleActive={() => handleToggleActive(row)}
										onDelete={() => handleDelete(row)}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			<div className={`${CARD} flex flex-col gap-4 p-4`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Outcome funnel
				</h2>
				<div className="flex flex-col gap-2 sm:flex-row sm:items-end">
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="w2a-campaign-filter" className={LABEL}>
							Campaign filter (optional)
						</label>
						<input
							id="w2a-campaign-filter"
							type="text"
							value={pendingCampaign}
							onChange={(e) => setPendingCampaign(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleApplyCampaign();
							}}
							className={INPUT}
							placeholder="summer_2026"
						/>
					</div>
					<button
						type="button"
						onClick={handleApplyCampaign}
						className={BTN_NEUTRAL}
					>
						Apply
					</button>
				</div>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<FunnelTile
						label="Deep link"
						count={funnel.deep_link_attempted}
						pct={funnelShares.deep_link_attempted}
					/>
					<FunnelTile
						label="App store"
						count={funnel.app_store_redirect}
						pct={funnelShares.app_store_redirect}
					/>
					<FunnelTile
						label="Web landing"
						count={funnel.web_landing}
						pct={funnelShares.web_landing}
					/>
				</div>
			</div>

			<div className={`${CARD} overflow-hidden`}>
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-base font-semibold text-zinc-900">
						Recent outcomes
					</h2>
					<span className="text-xs text-zinc-500">
						{formatInteger(outcomesQuery.data?.rows.length ?? 0)} shown
					</span>
				</div>
				{outcomesQuery.isLoading ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						Loading outcomes...
					</div>
				) : outcomesError ? (
					<div className="px-4 py-8 text-center text-sm text-red-600">
						{outcomesError.message || "Unable to load outcomes."}
					</div>
				) : (outcomesQuery.data?.rows.length ?? 0) === 0 ? (
					<div className="px-4 py-8 text-center text-sm text-zinc-500">
						No outcomes recorded yet.
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2">
										Campaign
									</th>
									<th scope="col" className="px-4 py-2">
										Outcome
									</th>
									<th scope="col" className="px-4 py-2">
										Click ID
									</th>
									<th scope="col" className="px-4 py-2">
										Timestamp
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{(outcomesQuery.data?.rows ?? []).map((outcome) => (
									<tr key={outcome.id}>
										<td className="px-4 py-2 font-mono text-xs text-zinc-900">
											{outcome.campaign_id}
										</td>
										<td className="px-4 py-2">
											<OutcomeBadge outcome={outcome.outcome} />
										</td>
										<td className="px-4 py-2 font-mono text-xs text-zinc-700">
											{outcome.click_id || (
												<span className="text-zinc-400">
													none
												</span>
											)}
										</td>
										<td className="px-4 py-2 text-zinc-700">
											{formatTimestamp(outcome.created_at)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Row — one handoff config.
// ---------------------------------------------------------------------------

interface ConfigRowProps {
	row: WebToAppConfigRow;
	busy: boolean;
	onToggleActive: () => void;
	onDelete: () => void;
}

function ConfigRow(props: ConfigRowProps) {
	const { row, busy, onToggleActive, onDelete } = props;
	const previewUrl = buildHandoffUrl(row.campaign_id);
	return (
		<tr>
			<td className="max-w-[200px] truncate px-4 py-2 font-mono text-xs text-zinc-900">
				{row.campaign_id}
			</td>
			<td className="max-w-[220px] truncate px-4 py-2 text-xs text-zinc-700">
				<div className="flex flex-col">
					<span className="font-mono">
						{row.ios_universal_link || row.ios_app_id || (
							<span className="text-zinc-400">not set</span>
						)}
					</span>
				</div>
			</td>
			<td className="max-w-[220px] truncate px-4 py-2 text-xs text-zinc-700">
				<div className="flex flex-col">
					<span className="font-mono">
						{row.android_app_link || row.android_package || (
							<span className="text-zinc-400">not set</span>
						)}
					</span>
				</div>
			</td>
			<td className="max-w-[220px] truncate px-4 py-2 text-xs text-zinc-700">
				<span className="font-mono">{row.web_fallback_url}</span>
			</td>
			<td className="px-4 py-2">
				<span className={row.active ? BADGE_ACTIVE : BADGE_INACTIVE}>
					{row.active ? "Active" : "Inactive"}
				</span>
			</td>
			<td className="px-4 py-2 text-right">
				<div className="flex flex-wrap justify-end gap-2">
					<a
						href={previewUrl}
						target="_blank"
						rel="noreferrer"
						className={BTN_NEUTRAL}
					>
						Preview
					</a>
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={onToggleActive}
						disabled={busy}
					>
						{row.active ? "Disable" : "Enable"}
					</button>
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onDelete}
						disabled={busy}
					>
						Delete
					</button>
				</div>
			</td>
		</tr>
	);
}

// ---------------------------------------------------------------------------
// Summary card — small stats row above the table.
// ---------------------------------------------------------------------------

interface SummaryRowProps {
	summary: ReturnType<typeof computeConfigSummary>;
}

function SummaryRow({ summary }: SummaryRowProps) {
	const items: Array<{
		label: string;
		value: string;
		tone: "primary" | "neutral";
	}> = [
		{
			label: "Total",
			value: formatInteger(summary.total),
			tone: "primary",
		},
		{
			label: "Active",
			value: formatInteger(summary.active),
			tone: "primary",
		},
		{
			label: "Inactive",
			value: formatInteger(summary.inactive),
			tone: "neutral",
		},
		{
			label: "iOS",
			value: formatInteger(summary.with_ios),
			tone: "neutral",
		},
		{
			label: "Android",
			value: formatInteger(summary.with_android),
			tone: "neutral",
		},
	];
	return (
		<div className={`${CARD} grid grid-cols-2 gap-4 p-4 sm:grid-cols-5`}>
			{items.map((item) => (
				<div key={item.label} className="flex flex-col">
					<span className="text-xs uppercase tracking-wide text-zinc-500">
						{item.label}
					</span>
					<span
						className={`text-2xl font-semibold ${
							item.tone === "primary"
								? "text-emerald-700"
								: "text-zinc-900"
						}`}
					>
						{item.value}
					</span>
				</div>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Funnel tile
// ---------------------------------------------------------------------------

interface FunnelTileProps {
	label: string;
	count: number;
	pct: number;
}

function FunnelTile({ label, count, pct }: FunnelTileProps) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-white p-3">
			<span className="text-xs uppercase tracking-wide text-zinc-500">
				{label}
			</span>
			<div className="flex items-baseline gap-2">
				<span className="text-xl font-semibold text-zinc-900">
					{formatInteger(count)}
				</span>
				<span className="text-xs text-zinc-500">
					{pct.toFixed(1)}%
				</span>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Outcome badge
// ---------------------------------------------------------------------------

function OutcomeBadge({ outcome }: { outcome: HandoffOutcome }) {
	const map: Record<HandoffOutcome, string> = {
		deep_link_attempted:
			"inline-flex items-center rounded-[4px] border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700",
		app_store_redirect:
			"inline-flex items-center rounded-[4px] border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700",
		web_landing:
			"inline-flex items-center rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-xs font-medium text-zinc-600",
	};
	return <span className={map[outcome]}>{formatOutcomeLabel(outcome)}</span>;
}

export default WebToAppPage;
